import React, { useEffect, useRef, useState } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import { ReactComponent as StoreIcon } from '../../assets/icons/ManagementDashboard_icon figma/_Blocks/uil_store.svg';
import { ReactComponent as AccountIcon } from '../../assets/icons/ManagementDashboard_icon figma/_Blocks/Account circle.svg';

/**
 * Store + stylist pickers (legacy TopBar logic). Dev-only on home; legacy TopBar uses variant="legacy".
 */
export default function DevFilterBar({
  variant = 'kuroco',
  className = '',
  hideStylist = false,
  storeValue,
  onStoreChange,
}) {
  const {
    stores,
    stylists,
    unregisteredStylists,
    selectedStore: contextStore,
    setSelectedStore: setContextStore,
    selectedStylist,
    setSelectedStylist,
    loading,
  } = useDashboard();
  // 店舗 tab passes its own value + handler so picking a store there never touches the
  // home's store/stylist. Without them the bar drives the shared dashboard filters as before.
  const ownsStore = typeof onStoreChange === 'function';
  const selectedStore = ownsStore ? storeValue || null : contextStore;
  const setSelectedStore = ownsStore ? onStoreChange : setContextStore;

  const [isStoreOpen, setIsStoreOpen] = useState(false);
  const [isStylistOpen, setIsStylistOpen] = useState(false);
  const storeRef = useRef(null);
  const stylistRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (storeRef.current && !storeRef.current.contains(e.target)) {
        setIsStoreOpen(false);
      }
      if (stylistRef.current && !stylistRef.current.contains(e.target)) {
        setIsStylistOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const isLegacy = variant === 'legacy';

  const storeBtnClass = isLegacy
    ? 'w-full md:w-auto md:min-w-[200px] md:max-w-[min(378.5px,42vw)] lg:max-w-none lg:w-[378.5px] min-h-[44px] h-14 px-6 py-4 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] overflow-hidden rounded-lg border border-[#E0E0E0] justify-start items-center inline-flex'
    : 'dev-filter-btn';

  const stylistBtnClass = isLegacy
    ? 'w-full md:w-auto md:min-w-[200px] md:max-w-[min(378.5px,42vw)] lg:max-w-none lg:w-[378.5px] min-h-[44px] h-14 px-6 py-4 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] overflow-hidden rounded-lg border border-[#E0E0E0] inline-flex justify-start items-center'
    : 'dev-filter-btn';

  const wrapClass = isLegacy
    ? 'relative w-full md:w-auto'
    : 'dev-filter-item';

  const rootClass = isLegacy
    ? className
    : `dev-filter-bar${className ? ` ${className}` : ''}`;

  const storeLabel = loading.stores ? '読み込み中...' : (selectedStore || '店舗を選択');
  const stylistLabel = loading.stylists ? '読み込み中...' : (selectedStylist || 'スタイリストを選択');

  return (
    <div className={rootClass}>
      <div className={wrapClass} ref={storeRef}>
        <button
          type="button"
          onClick={() => setIsStoreOpen(!isStoreOpen)}
          className={storeBtnClass}
          aria-expanded={isStoreOpen}
          aria-haspopup="listbox"
        >
          {isLegacy ? (
            <div className="flex-1 justify-between items-center flex">
              <div className="justify-start items-center gap-2 flex">
                <div className="w-6 h-6 relative overflow-hidden">
                  <StoreIcon className="w-5 h-5 left-[2px] top-[2px] absolute" />
                </div>
                <div className="text-[#333333] text-base font-medium font-['Noto Sans'] leading-6">
                  {storeLabel}
                </div>
              </div>
              <div className="text-[#33564B] text-base font-medium font-['Noto Sans'] underline leading-6">店舗を選択</div>
            </div>
          ) : (
            <>
              <StoreIcon className="dev-filter-ic" aria-hidden="true" />
              <span className="dev-filter-label">{storeLabel}</span>
              <span className="dev-filter-action">店舗</span>
            </>
          )}
        </button>

        {isStoreOpen && (
          <div className={isLegacy ? 'absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto' : 'dev-filter-menu'}>
            {stores.length === 0 ? (
              <div className={isLegacy ? 'px-4 py-3 text-gray-500 text-sm' : 'dev-filter-empty'}>店舗がありません</div>
            ) : (
              stores.map((store) => (
                <button
                  key={store}
                  type="button"
                  onClick={() => {
                    setSelectedStore(store);
                    setIsStoreOpen(false);
                  }}
                  className={
                    isLegacy
                      ? `w-full px-4 py-3 text-left hover:bg-gray-100 text-sm ${
                          selectedStore === store ? 'bg-[#33564B] text-white' : 'text-gray-700'
                        }`
                      : `dev-filter-option${selectedStore === store ? ' is-active' : ''}`
                  }
                >
                  {store}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {!hideStylist && (
      <div className={wrapClass} ref={stylistRef}>
        <button
          type="button"
          onClick={() => setIsStylistOpen(!isStylistOpen)}
          className={stylistBtnClass}
          aria-expanded={isStylistOpen}
          aria-haspopup="listbox"
        >
          {isLegacy ? (
            <div className="flex-1 flex justify-between items-center">
              <div className="flex justify-start items-center gap-2">
                <AccountIcon className="w-6 h-6" />
                <div className="text-[#333333] text-base font-medium font-['Noto Sans'] leading-6">
                  {stylistLabel}
                </div>
              </div>
              <div className="text-[#33564B] text-base font-medium font-['Noto Sans'] underline leading-6">スタイリストを選択</div>
            </div>
          ) : (
            <>
              <AccountIcon className="dev-filter-ic" aria-hidden="true" />
              <span className="dev-filter-label">{stylistLabel}</span>
              <span className="dev-filter-action">担当</span>
            </>
          )}
        </button>

        {isStylistOpen && (
          <div className={isLegacy ? 'absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto' : 'dev-filter-menu'}>
            <button
              type="button"
              onClick={() => {
                setSelectedStylist(null);
                setIsStylistOpen(false);
              }}
              className={
                isLegacy
                  ? `w-full px-4 py-3 text-left hover:bg-gray-100 text-sm ${
                      selectedStylist === null ? 'bg-[#33564B] text-white' : 'text-gray-700'
                    }`
                  : `dev-filter-option${selectedStylist === null ? ' is-active' : ''}`
              }
            >
              全スタイリスト
            </button>
            {stylists.length === 0 ? (
              <div className={isLegacy ? 'px-4 py-3 text-gray-500 text-sm' : 'dev-filter-empty'}>
                {selectedStore ? 'スタイリストがありません' : '店舗を選択してください'}
              </div>
            ) : (
              stylists.map((stylist) => (
                <button
                  key={stylist}
                  type="button"
                  onClick={() => {
                    setSelectedStylist(stylist);
                    setIsStylistOpen(false);
                  }}
                  className={
                    isLegacy
                      ? `w-full px-4 py-3 text-left hover:bg-gray-100 text-sm ${
                          selectedStylist === stylist ? 'bg-[#33564B] text-white' : 'text-gray-700'
                        }`
                      : `dev-filter-option${selectedStylist === stylist ? ' is-active' : ''}`
                  }
                >
                  {stylist}
                  {unregisteredStylists?.includes(stylist) ? '（未登録）' : ''}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
